(function () {
  'use strict';

  // Focus areas ("Health is multifaceted"): the photo's labels start outside
  // the photo and slide in to their place the first time it is properly on
  // screen. The CSS holds the start state and the transition; this file works
  // out how far out each label has to start and says when to go.

  // The labels come in once this much of the photo is visible — nearly all of
  // it, so the whole entrance is watched rather than played at the bottom
  // edge of the screen while the visitor is still scrolling towards it.
  var VISIBLE_RATIO = 0.9;
  // On a viewport too short to show that much of the photo, settle for this
  // share of the viewport's height instead.
  var VIEWPORT_SHARE = 0.75;
  // Extra distance past the photo's edge, so no sliver of a label shows.
  var CLEARANCE = 8;
  var RESIZE_DEBOUNCE_MS = 150;
  // Longest the entrance waits for the page's load event.
  var LOAD_WAIT_MS = 4000;

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  // Each label travels along the line from the photo's centre through its own
  // centre ("outside → in"). The start offset is how far along that line it
  // must go to be completely past the nearest edge it is heading for.
  // offset* values ignore the transform the label is currently held at.
  function setOffsets(media, labels) {
    var width = media.offsetWidth;
    var height = media.offsetHeight;
    if (!width || !height) return false; // photo hidden (tablet / mobile)

    labels.forEach(function (label) {
      var left = label.offsetLeft;
      var top = label.offsetTop;
      var w = label.offsetWidth;
      var h = label.offsetHeight;

      var dx = left + w / 2 - width / 2;
      var dy = top + h / 2 - height / 2;
      var length = Math.sqrt(dx * dx + dy * dy);
      // Dead centre has no direction of its own: send it up.
      var ux = length ? dx / length : 0;
      var uy = length ? dy / length : -1;

      // Distance to clear the left/right edge and the top/bottom edge; the
      // label is out as soon as it has cleared either.
      var exitX = ux > 0 ? (width - left) / ux : ux < 0 ? (left + w) / -ux : Infinity;
      var exitY = uy > 0 ? (height - top) / uy : uy < 0 ? (top + h) / -uy : Infinity;
      var distance = Math.min(exitX, exitY) + CLEARANCE;

      label.style.setProperty('--enter-x', (ux * distance).toFixed(1) + 'px');
      label.style.setProperty('--enter-y', (uy * distance).toFixed(1) + 'px');
    });
    return true;
  }

  function setup(list) {
    if (list._focusLabelsInit) return;
    list._focusLabelsInit = true;

    var media = list.closest('.focus-areas__media');
    var labels = list.querySelectorAll('.focus-areas__label');
    if (!media || !labels.length) return;

    // No entrance: leave the labels where the CSS draws them.
    if (reduceMotion.matches || !('IntersectionObserver' in window)) return;

    var revealed = false;
    var observer = null;
    var resizeTimer;
    var loadTimer;

    list.classList.add('is-armed');

    function prepare() {
      if (setOffsets(media, labels)) list.classList.add('is-ready');
    }

    function reveal() {
      if (revealed) return;
      revealed = true;
      if (observer) observer.disconnect();
      prepare();
      // Commit the start position before switching to the end state, or the
      // browser would have nothing to transition from.
      void list.offsetWidth;
      list.classList.add('is-ready', 'is-inview');
    }

    // How much of the photo has to be visible, capped by what this viewport
    // can actually show.
    function threshold() {
      var height = media.offsetHeight;
      if (!height) return VISIBLE_RATIO;
      return Math.min(VISIBLE_RATIO, (window.innerHeight * VIEWPORT_SHARE) / height);
    }

    function observe() {
      if (revealed) return;
      if (observer) observer.disconnect();
      window.clearTimeout(loadTimer);
      observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) reveal();
        });
      }, { threshold: threshold() });
      observer.observe(media);
    }

    prepare();

    // The offsets and the threshold depend on the photo's and the viewport's
    // size; until the labels have come in, keep both right.
    window.addEventListener('resize', function () {
      if (revealed) return;
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        prepare();
        observe();
      }, RESIZE_DEBOUNCE_MS);
    });

    // Start watching once the page has finished loading: a photo already on
    // screen (reload, back button) should not play its entrance while the
    // page is still painting. A slow third-party resource must not hold the
    // labels back for long, though.
    if (document.readyState === 'complete') {
      observe();
    } else {
      window.addEventListener('load', observe, { once: true });
      loadTimer = window.setTimeout(observe, LOAD_WAIT_MS);
    }
  }

  function init(scope) {
    (scope || document).querySelectorAll('[data-focus-labels]').forEach(setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  // Theme Editor / hot reload swap the section's markup without re-running
  // this file.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
